import { DeleteExamDialog, ExamDialog } from '@/components/exams';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import { AttachStudentDialog } from '@/components/students';
import {
  useArchiveCourse,
  useAttachStudentToCourse,
  useCourse,
  useDeleteCourse,
  useDetachStudentFromCourse,
  useUpdateCourse,
} from '@/hooks/useCourses';
import { useCreateExam } from '@/hooks/useExams';
import type { AppContext } from '@/main';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  AvatarBeam,
  CourseDialog,
  DeleteCourseDialog,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ui/components';
import { Button } from '@ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@ui/components/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/components/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@ui/components/empty';
import { Input } from '@ui/components/input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@ui/components/item';
import {
  Archive,
  BookOpen,
  Calendar,
  Edit2,
  ExternalLinkIcon,
  FileText,
  MoreVertical,
  Plus,
  Save,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_dashboard/courses/$courseId')<AppContext>({
  component: RouteComponent,
  loader: async ({ params }) => {
    return {
      crumb: `Course ${params.courseId} Details`,
    };
  },
});

function RouteComponent() {
  const { courseId } = Route.useParams();
  const navigate = useNavigate();
  const { data: course, isLoading, error } = useCourse(courseId);
  const updateCourseMutation = useUpdateCourse();
  const deleteCourseMutation = useDeleteCourse();
  const archiveCourseMutation = useArchiveCourse();
  const attachStudentMutation = useAttachStudentToCourse();
  const detachStudentMutation = useDetachStudentFromCourse();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');

  // Dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAttachStudentDialogOpen, setIsAttachStudentDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    courseId: null as string | null,
    courseName: '',
  });
  const [isCreateExamDialogOpen, setIsCreateExamDialogOpen] = useState(false);
  const [examDeleteDialog, setExamDeleteDialog] = useState({
    open: false,
    examId: null as string | null,
    examName: '',
  });

  const createExamMutation = useCreateExam(courseId);

  const handleUpdateCourse = async (data: { name: string; description: string }) => {
    await updateCourseMutation.mutateAsync({ courseId, data });
  };

  const handleDeleteCourse = async (courseId: string) => {
    await deleteCourseMutation.mutateAsync(courseId);
    // Navigate back to courses list
    window.location.href = '/courses';
  };

  const handleCreateExam = async (data: { name: string }) => {
    await createExamMutation.mutateAsync(data);
  };

  const handleAttachStudent = async (studentId: string) => {
    await attachStudentMutation.mutateAsync({ courseId, studentId, type: 'attach' });
  };

  const handleDetachStudent = async (studentId: string) => {
    await detachStudentMutation.mutateAsync({ courseId, studentId });
  };

  const openDeleteDialog = () => {
    if (!course) return;
    setDeleteDialog({
      open: true,
      courseId: course.id,
      courseName: course.name,
    });
  };

  const handleSave = async () => {
    if (!editName.trim()) return;
    try {
      await updateCourseMutation.mutateAsync({ courseId, data: { name: editName } });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update course:', error);
    }
  };

  const handleArchive = async () => {
    if (confirm('Are you sure you want to archive this course?')) {
      try {
        await archiveCourseMutation.mutateAsync(courseId);
      } catch (error) {
        console.error('Failed to archive course:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading course details...</div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load course details</div>
        <Button asChild variant="outline">
          <Link to="/courses">Back to Courses</Link>
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          {isEditing ? (
            <div className="flex items-center space-x-2">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="text-2xl font-bold"
                autoFocus
              />
              <Button size="sm" onClick={handleSave} disabled={updateCourseMutation.isPending}>
                <Save className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <PageHeaderTitle>{course.name}</PageHeaderTitle>
              <Button size="sm" variant="ghost" onClick={() => setIsEditDialogOpen(true)}>
                <Edit2 className="h-4 w-4" />
              </Button>
              {course.archived && (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  <Archive className="h-3 w-3 mr-1" />
                  Archived
                </span>
              )}
            </div>
          )}
          <PageHeaderDescription>
            <span className="text-xs font-mono bg-muted px-2 py-1 rounded">{course.id}</span>
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button
            variant="outline"
            disabled={course.archived}
            onClick={handleArchive}
            className="gap-2"
          >
            <Archive className="h-4 w-4" />
            {course.archived ? 'Archived' : 'Archive'}
          </Button>
          <Button variant="destructive" onClick={openDeleteDialog} className="gap-2">
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button onClick={() => setIsEditDialogOpen(true)} className="gap-2">
            <Edit2 className="h-4 w-4" />
            Edit
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="exams">Exams</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Students</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{course.students?.length || 0}</div>
                <p className="text-xs text-muted-foreground">Enrolled students</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Exams</CardTitle>
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{course.exams?.length || 0}</div>
                <p className="text-xs text-muted-foreground">Available exams</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Status</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${course.archived ? 'text-yellow-600' : 'text-green-600'}`}
                >
                  {course.archived ? 'Archived' : 'Active'}
                </div>
                <p className="text-xs text-muted-foreground">Course status</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="students">
          <Card>
            <CardHeader>
              <CardTitle>Students</CardTitle>
              <CardDescription>Students enrolled in this course</CardDescription>
              <CardAction className="flex justify-end">
                <Button
                  onClick={() => setIsAttachStudentDialogOpen(true)}
                  className="gap-2"
                  disabled={course.archived}
                >
                  <Plus className="h-4 w-4" />
                  Add Students
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent>
              {course.students && course.students.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 w-full">
                  {course.students.map((student) => (
                    <Card key={`students#${student.id}`}>
                      <CardHeader className="items-center">
                        <CardTitle className="flex gap-2 w-fit items-center">
                          <AvatarBeam id={student.id} name={student.name} />
                          {student.name}
                        </CardTitle>
                        <CardDescription>#{student.externalId || 'No external ID'}</CardDescription>
                        <CardAction className="flex items-center justify-center h-full">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate({
                                    to: '/students/$studentId',
                                    params: { studentId: student.id },
                                  });
                                }}
                              >
                                <ExternalLinkIcon className="h-4 w-4" />
                                View Student
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                variant="destructive"
                                onClick={() => handleDetachStudent(student.id)}
                              >
                                <X className="h-4 w-4" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </CardAction>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              ) : (
                <Empty className="border border-dashed">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Users />
                    </EmptyMedia>
                    <EmptyTitle>Empty</EmptyTitle>
                    <EmptyDescription>No students assigned to this course</EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAttachStudentDialogOpen(true)}
                      disabled={course.archived}
                    >
                      <Plus className="h-4 w-4" />
                      Add Students
                    </Button>
                  </EmptyContent>
                </Empty>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="exams">
          <Card>
            <CardHeader>
              <CardTitle>Exams</CardTitle>
              <CardDescription>Exams associated with this course</CardDescription>
              <CardAction className="flex justify-end">
                <Button onClick={() => setIsCreateExamDialogOpen(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Exam
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent>
              {course.exams && course.exams.length > 0 ? (
                <div className="flex w-full flex-col gap-2">
                  {course.exams.map((exam) => (
                    <Item key={`exams#${exam}`} variant="outline" asChild>
                      <Link to="/exams/$examId" params={{ examId: exam.id }}>
                        <ItemMedia variant="icon" className="size-16">
                          <BookOpen />
                        </ItemMedia>
                        <ItemContent>
                          <ItemTitle>{exam.name}</ItemTitle>
                          <ItemDescription>{exam.archived ? 'Archived' : 'Active'}</ItemDescription>
                        </ItemContent>
                        <ItemMedia variant="icon" className="size-16">
                          <Users />
                          {exam.studentCount}
                        </ItemMedia>
                        <ItemMedia variant="icon" className="size-16">
                          <FileText />
                          {exam.questionCount}
                        </ItemMedia>
                        <ItemActions>
                          <ExternalLinkIcon className="size-4" />
                        </ItemActions>
                      </Link>
                    </Item>
                  ))}
                </div>
              ) : (
                <Empty className="border border-dashed">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <BookOpen />
                    </EmptyMedia>
                    <EmptyTitle>Empty</EmptyTitle>
                    <EmptyDescription>No exams available for this course</EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsCreateExamDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4" />
                      Create Exam
                    </Button>
                  </EmptyContent>
                </Empty>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="settings"></TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CourseDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        course={course}
        onSave={handleUpdateCourse}
      />

      <DeleteCourseDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        courseId={deleteDialog.courseId}
        courseName={deleteDialog.courseName}
        onDelete={handleDeleteCourse}
      />

      <ExamDialog
        open={isCreateExamDialogOpen}
        onOpenChange={setIsCreateExamDialogOpen}
        onSave={handleCreateExam}
      />

      <DeleteExamDialog
        open={examDeleteDialog.open}
        onOpenChange={(open) => setExamDeleteDialog((prev) => ({ ...prev, open }))}
        examId={examDeleteDialog.examId}
        examName={examDeleteDialog.examName}
        onDelete={async (examId) => {
          // Handle exam deletion
          console.log('Delete exam:', examId);
        }}
      />

      <AttachStudentDialog
        open={isAttachStudentDialogOpen}
        onOpenChange={setIsAttachStudentDialogOpen}
        title="Attach Student to Course"
        description="Select a student to attach to this course."
        excludeStudentIds={course.students.map((student) => student.id) || []}
        onAttach={handleAttachStudent}
      />
    </Page>
  );
}
